import { useIsMutating } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { useState, useMemo } from "react";
import AutomationSetting from "./componentes/AutomationSetting";
import AutomationSummary from "./componentes/AutomationSummary";
import AIActivity from "./componentes/AIActivity";
import RANoDataFound from "./componentes/RANoDataFound";
import GBPConnectionGuard from "../local-seo/components/GBPConnectionGuard";
import ReviewAutomationCommon from "./componentes/ReviewAutomationCommon";
import { useGetReviewAutomationDashboard, useGetReviewAutomationSettings } from "./hooks";
import { StripeLoader } from 'src/common/StripeLoader';
import OrbitAIGuard from '../Components/OrbitAIGuard';
import UpgradeToOrbitAI from '../Components/UpgradeToOrbitAI';
import ReviewAutomationBreadcrumbs from "./componentes/ReviewAutomationBreadcrumbs";
import { useOrbitAISSE } from "../hooks/useOrbitAISSE";
import { Card as SEOCard } from "src/components/UI/Card";
import { Card } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import { getProfileDisabledState } from "../hooks/useIsConfiguredToOtherSite";
import { useGetUserAgentDetails } from "../hooks/useGetUserAgentDetails";
import useSiteGBP1to1Mapping, { IGBPLocation } from "../hooks/useSiteGBP1to1Maping";

import { keywordResearchSubmenuTitles } from 'src/common/config/TextMessage';
import OrbitAgentSiteGuard from '../Components/OrbitAgentSiteGuard';

function ReviewAutomationFallback() {
  return (
    <div
      className="-mt-8 pt-8 pb-6"
      style={{
        minHeight: 'calc(100vh - 51px)',
        backgroundColor: 'var(--color-bg-cream)'
      }}
    >
      <div className="flex justify-between items-center mb-3">
        <ReviewAutomationBreadcrumbs />
      </div>
      <SEOCard>
        <div className="px-6">
          <p className="text-primary font-semibold text-3xl">Review Automation</p>
          <div className="w-[600px] mx-auto my-10">
            <UpgradeToOrbitAI />
          </div>
        </div>
      </SEOCard>
    </div>
  );
}

function ReviewAutomationContent() {
  const { _id: reduxSiteId } = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  ) ?? {};

  useOrbitAISSE(reduxSiteId);

  const [manuallySelectedProfile, setManuallySelectedProfile] = useState<IGBPLocation | null>(null);

  const siteGBP1to1Configurations = useSiteGBP1to1Mapping({ siteId: reduxSiteId });

  const selectedProfile = useMemo(() => {
    if (manuallySelectedProfile) return manuallySelectedProfile;
    if (!siteGBP1to1Configurations || siteGBP1to1Configurations.isConfigurationError) return null;
    return siteGBP1to1Configurations.connectedGBPAccount ?? null;
  }, [manuallySelectedProfile, siteGBP1to1Configurations]);

  const onProfileSelect = (businessProfile: IGBPLocation) => {
    setManuallySelectedProfile(businessProfile);
  };

  const accountId = selectedProfile?.accountId ? String(selectedProfile.accountId) : "";
  const locationId = selectedProfile?.name
    ? (selectedProfile.name.includes('/') ? selectedProfile.name.split('/')[1] : selectedProfile.name)
    : String(selectedProfile?.locationId || "");

  // 1. Fetch Settings API (/api/v2/review-automation/{accountId}/{locationId}/settings)
  const {
    data: settingsData,
    isLoading: isSettingsLoading,
    isFetching: isSettingsFetching,
  } = useGetReviewAutomationSettings({
    accountId,
    locationId,
    siteId: reduxSiteId,
  });

  const { data: userAgentDetails } = useGetUserAgentDetails(reduxSiteId);
  const localSeoApiData = userAgentDetails?.response?.local_seo;
  const reviewAutomationApiData = userAgentDetails?.response?.review_automation;

  const selectedProfileDisabledState = useMemo(() => {
    return getProfileDisabledState(
      selectedProfile,
      localSeoApiData,
      reduxSiteId,
      siteGBP1to1Configurations.connectedSite?.url,
      siteGBP1to1Configurations.siteList,
      reviewAutomationApiData
    );
  }, [selectedProfile, localSeoApiData, reviewAutomationApiData, reduxSiteId, siteGBP1to1Configurations]);

  const isConfiguredToOtherSite = selectedProfileDisabledState.disabled;
  const siteURL = selectedProfileDisabledState.tooltip;

  const isSelectedProfileMapped = Boolean(
    selectedProfile && (
      // No GBP connected yet — allow first-time binding with any valid profile
      !siteGBP1to1Configurations.connectedGBPAccount ||
      // GBP already connected — only allow the matching profile
      (
        String(siteGBP1to1Configurations.connectedGBPAccount.accountId) === String(selectedProfile.accountId) &&
        (
          siteGBP1to1Configurations.connectedGBPAccount.name === selectedProfile.name ||
          String(siteGBP1to1Configurations.connectedGBPAccount.locationId) === String(selectedProfile.locationId)
        )
      )
    )
  );

  const isBlocked = isConfiguredToOtherSite || siteGBP1to1Configurations.isConfigurationError || !isSelectedProfileMapped;

  // 2. Fetch Dashboard API (/api/v2/review-automation/{accountId}/{locationId}/dashboard)
  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    isFetching: isDashboardFetching,
  } = useGetReviewAutomationDashboard({
    siteId: reduxSiteId,
    accountId,
    locationId,
  });

  // 3. Track if saving settings mutation is currently in-flight
  const isSavingSettings = useIsMutating({ mutationKey: ["saveReviewAutomationSettings"] }) > 0;

  const isEnabled = !isBlocked && (settingsData?.settings?.enabled ?? dashboardData?.settings?.enabled ?? false);

  const isInitialOrSettingsLoading =
    Boolean(accountId && locationId && (isSettingsLoading || isSettingsFetching)) ||
    isSavingSettings;

  const isContentLoading = Boolean(accountId && locationId && (isDashboardLoading || isDashboardFetching));

  const rawSyncTime =
    dashboardData?.lastSyncedAt ||
    dashboardData?.settings?.last_synced_at ||
    settingsData?.settings?.last_synced_at ||
    reviewAutomationApiData?.last_synced_at;

  const lastRunTimestamp = useMemo(() => {
    if (!isEnabled) return null;
    if (rawSyncTime && typeof rawSyncTime === 'number' && rawSyncTime > 0) {
      return rawSyncTime;
    }
    return null;
  }, [isEnabled, rawSyncTime]);

  return (
    <div
      className="-mt-8 pt-8 pb-5"
      style={{
        minHeight: 'calc(100vh - 51px)',
        backgroundColor: 'var(--color-bg-cream)'
      }}
    >
      {/* Always visible header with breadcrumbs */}
      <div>
        <ReviewAutomationCommon
          onSelect={onProfileSelect}
          selectedProfile={selectedProfile}
          lastRunTime={lastRunTimestamp}
        />
      </div>

      <GBPConnectionGuard>
        <Card sx={{ mt: 2, boxShadow: "2px 2px 12px 2px rgba(0,0,0,0.06)" }}>
          <AutomationSetting
            accountId={accountId}
            locationId={locationId}
            settingsData={settingsData}
            isSettingsLoading={isInitialOrSettingsLoading}
            isBlocked={isBlocked}
            isMapped={isSelectedProfileMapped}
          />
        </Card>

        {isInitialOrSettingsLoading ? (
          <div className="mt-4">
            <StripeLoader />
          </div>
        ) : isEnabled ? (
          isContentLoading ? (
            <div className="mt-4">
              <StripeLoader />
            </div>
          ) : (
            <>
              <Card sx={{ mt: 3, boxShadow: "2px 2px 12px 2px rgba(0,0,0,0.06)" }}>
                <AutomationSummary summaryData={dashboardData?.summary} />
              </Card>

              <Card sx={{ mt: 3, boxShadow: "2px 2px 12px 2px rgba(0,0,0,0.06)" }}>
                <AIActivity activityData={dashboardData?.activity} />
              </Card>
            </>
          )
        ) : (
          <RANoDataFound
            isEnabled={isEnabled}
            alreadyConfigured={{
              isConfiguredToOtherSite,
              siteURL,
              isSiteConfiguredToOtherGbp: Boolean(siteGBP1to1Configurations?.isConfigurationError),
              configuredGbpName: siteGBP1to1Configurations?.connectedGBPAccount?.title || '',
              isGBPConnected: siteGBP1to1Configurations?.isGBPConnected,
              isNotMapped: !isSelectedProfileMapped,
            }}
          />
        )}
      </GBPConnectionGuard>
    </div>
  );
}

function ReviewAutomationPage() {
  return (
    <>
      <Helmet>
        <title>SEOSpace | Review Automation</title>
      </Helmet>

      <OrbitAIGuard fallback={<ReviewAutomationFallback />}>
        <OrbitAgentSiteGuard
          title={keywordResearchSubmenuTitles.reviewAutomation}
        >
          <ReviewAutomationContent />
        </OrbitAgentSiteGuard>
      </OrbitAIGuard>
    </>
  );
}

export default ReviewAutomationPage;
